import type {
  IFilterVisitorJourney,
  IFilterCampaignValue,
  IFilterVisitorJourneyValue,
} from '~/modules/heatmap/types';
import type { IBasePathAnalyticPageWithChild } from '../types';

import { useHeatmapModalMonitor } from '~/modules/heatmap/hooks/useHeatmapModalMonitor';
import { EHeatmapBaseFilterType, EHeatmapFilterType } from '~/modules/heatmap/types';
import { useHeatmapFilterBuildParams } from '~/modules/heatmap';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { usePathAnalysisCampaignStore } from '../stores';
import type { TemplateType } from '~/types/template';
import { usePathAnalysisContext } from '../contexts';
import { useCallback } from 'react';

const buildJourneyPage = (
  page: IBasePathAnalyticPageWithChild,
  index: number,
  {
    heatmapLevel,
    campaignData,
  }: { heatmapLevel: number; campaignData: IFilterCampaignValue | undefined },
): IFilterVisitorJourneyValue => ({
  id: page.id,
  path: page.locationPath || '',
  level: index + 1,
  isHeatmap: index === heatmapLevel,
  ...(heatmapLevel > 0 && index === 0 && campaignData && { subFilter: { campaignData } }),
});

export const usePathAnalysisHeatmap = () => {
  const selectedVariantType = usePathAnalysisCampaignStore((state) => state.selectedVariantType);

  const { dateTimePicked } = useMetricsTimeFilter();
  const { mode, campaignID, campaignVariantID, campaignGroupID } = usePathAnalysisContext();

  const { onOpenModal } = useHeatmapModalMonitor();
  const { buildSearchParams } = useHeatmapFilterBuildParams();

  const handleViewHeatmap = useCallback(
    (level: number, pages: IBasePathAnalyticPageWithChild[]) => {
      const levelPages = pages.slice(0, level + 1);
      const clickedPage = levelPages[level];
      if (!clickedPage) return;

      const campaignData: IFilterCampaignValue | undefined = campaignID
        ? {
            campaignId: campaignID,
            campaignGroupId: campaignGroupID,
            campaignVariantId: campaignVariantID || '',
            campaignVariantType: selectedVariantType,
          }
        : undefined;

      const pageType = clickedPage.pageType as TemplateType | undefined;
      const locationPath = clickedPage.locationPath || '';
      const isViewFirstLevel = level === 0;
      const visitorJourney: IFilterVisitorJourney = {
        value: levelPages.map((page, index) =>
          buildJourneyPage(page, index, { heatmapLevel: level, campaignData }),
        ),
      };

      const data = buildSearchParams(
        {
          [EHeatmapFilterType.VISITOR_JOURNEY]: visitorJourney,
          ...(isViewFirstLevel &&
            campaignData && { [EHeatmapFilterType.CAMPAIGN]: { value: campaignData } }),
        },
        {
          [EHeatmapBaseFilterType.ANALYTIC_MODE]: { value: mode },
          [EHeatmapBaseFilterType.DATE_RANGE]: { value: dateTimePicked },
          [EHeatmapBaseFilterType.URL_PATH]: {
            value: { path: locationPath, pageType },
          },
        },
      );

      onOpenModal(data.toString());
    },
    [
      mode,
      campaignID,
      dateTimePicked,
      campaignGroupID,
      campaignVariantID,
      selectedVariantType,
      onOpenModal,
      buildSearchParams,
    ],
  );

  return {
    handleViewHeatmap,
  };
};
